import type { SearchResult } from '@liniya/api-client';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/api/client';
import { Avatar } from '@/components/Avatar';
import { queryKeys } from '@liniya/client-core';
import { useDebouncedValue } from '@liniya/client-core';

interface Props {
  query: string;
  onPick: (result: SearchResult) => void;
  busy?: boolean;
}

const MINIMUM_QUERY_LENGTH = 2;

/**
 * Server-side lookup of people, groups and channels.
 *
 * Typing is debounced because the endpoint is rate limited and a search per
 * keystroke would exhaust a user's allowance mid-word. Below two characters
 * nothing is requested at all: the result would be meaningless and the request
 * would still count.
 */
export function UserSearch({ query, onPick, busy = false }: Props) {
  const debounced = useDebouncedValue(query.trim(), 350);
  const enabled = debounced.length >= MINIMUM_QUERY_LENGTH;

  const results = useQuery({
    queryKey: queryKeys.search(debounced),
    queryFn: ({ signal }) => api.search.query(debounced, { limit: 8, signal }),
    enabled,
    staleTime: 30_000,
  });

  if (!enabled) return null;

  if (results.isPending) {
    return (
      <div className="user-search-results">
        <p className="user-search-message">Ищем…</p>
      </div>
    );
  }

  if (results.isError) {
    return (
      <div className="user-search-results">
        <p className="user-search-message">Поиск временно недоступен.</p>
      </div>
    );
  }

  if (results.data.length === 0) {
    return (
      <div className="user-search-results">
        <p className="user-search-message">Ничего не найдено</p>
      </div>
    );
  }

  return (
    <div className="user-search-results" role="listbox" aria-label="Результаты поиска">
      {results.data.map((result) => (
        <button
          key={`${result.type}:${result.id}`}
          type="button"
          className="user-search-result"
          role="option"
          aria-selected="false"
          disabled={busy}
          onClick={() => onPick(result)}
        >
          <Avatar
            name={result.title}
            kind={result.type === 'user' ? 'direct' : result.type}
            size="small"
          />
          <span className="chat-copy">
            <strong>{result.title}</strong>
            <span className="chat-preview">
              {result.type === 'user'
                ? result.username
                  ? `@${result.username}`
                  : 'Открыть личный чат'
                : result.type === 'channel'
                  ? 'Канал'
                  : 'Группа'}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}
